import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "shared/components/FormElements/Button";
import AreYouSureModal from "components/shared/AreYouSureModal";
import { useHttpClient } from "shared/hooks/http-hook";
import { useBackend } from "shared/hooks/backend-hook";
import { checkIsPickUp } from "shared/utility/types";
import { GetHoursMinutesFromDate, subtractSecondsFromTime, addSecondsToTime } from "shared/util/time";

const safeGetTime = (dateValue, fallback) => {
  if (!dateValue) return fallback;
  try {
    return GetHoursMinutesFromDate(dateValue);
  } catch (e) {
    return fallback;
  }
};

const getOppositeRouteType = (routeType) => {
  if (routeType === "PICK_UP" || routeType?.toString() === "100") return "DROP_OFF";
  if (routeType === "DROP_OFF" || routeType?.toString() === "200") return "PICK_UP";
  return null;
};

const toId = (x) => (x && typeof x === "object" ? String(x.id || x._id) : x ? String(x) : null);

const reversedIds = (arr) => (Array.isArray(arr) ? arr.map(toId).filter(Boolean) : []).slice().reverse();

// ---- Otomatik optimize yardımcıları (karşıt servis, haritadaki "Optimize Et" ile aynı kurgu) ----

const isValidGeo = (loc) => {
  if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number") return false;
  if (Number.isNaN(loc.lat) || Number.isNaN(loc.lng)) return false;
  if (loc.lat === -1.1 && loc.lng === -1.1) return false;
  if (loc.lat === 0 && loc.lng === 0) return false;
  return Math.abs(loc.lat) <= 90 && Math.abs(loc.lng) <= 180;
};

const isValidHHmm = (input) => {
  if (typeof input !== "string" || !input) return false;
  const m = input.match(/^(\d{1,2}):(\d{1,2})/);
  if (!m) return false;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  return h >= 0 && h <= 23 && mm >= 0 && mm <= 59;
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Karşıt servis için optimize edilmiş yolcu sırası + transfer saatleri + metrikleri hesaplar.
 * Haritadaki "Optimize Et" akışının birebir karşılığı: en uzak durak uç nokta, okul diğer uç,
 * kalanlar Google optimize:true waypoint'leri; saatler anchor'dan (routeClientArrivalTime)
 * kümülatif bacak süreleriyle türetilir.
 *
 * Herhangi bir girdi eksikse/istek başarısızsa `null` döner — çağıran güvenli yola düşer
 * (servis optimize edilmeden, saatler KOPYALANMADAN oluşturulur).
 */
const computeOptimizedOppositeExtras = async ({
  loadedItem,
  oppositeIsPickUp,
  getDirectionSingle,
  getDirectionsWithWaypoints,
}) => {
  try {
    const schoolLoc = loadedItem.school?.location;
    if (!isValidGeo(schoolLoc)) return null;

    const users = (loadedItem.users || []).filter((u) => u && typeof u === "object");
    if (users.length === 0) return null;

    // Karşıt yönün konumları: toplama → biniş, dağıtma → iniş.
    // Tek bir yolcunun bile konumu eksikse sıralama eksik üretilir — güvenli yola düş.
    const stops = [];
    for (const u of users) {
      const loc = oppositeIsPickUp ? u.locationgeton : u.locationgetoff;
      if (!isValidGeo(loc)) return null;
      stops.push({ id: String(u.id || u._id), location: { lat: loc.lat, lng: loc.lng } });
    }
    if (stops.length > 26) return null; // Google 25 ara-waypoint sınırı

    // En uzak durak (haversine — ekstra Directions çağrısı yapmadan)
    let farthest = stops[0];
    let bestKm = -1;
    for (const s of stops) {
      const km = haversineKm(schoolLoc.lat, schoolLoc.lng, s.location.lat, s.location.lng);
      if (km > bestKm) {
        bestKm = km;
        farthest = s;
      }
    }

    const schoolPoint = { id: "school", location: { lat: schoolLoc.lat, lng: schoolLoc.lng } };
    const origin = oppositeIsPickUp ? farthest : schoolPoint;
    const destination = oppositeIsPickUp ? schoolPoint : farthest;
    const waypoints = stops.filter((s) => s.id !== farthest.id).map((s) => ({ id: s.id, location: s.location }));

    let legs;
    if (waypoints.length === 0) {
      const d = await getDirectionSingle(origin, destination);
      if (!d || typeof d.duration !== "number") return null;
      legs = [{ ...d, id: origin.id }];
    } else {
      legs = await getDirectionsWithWaypoints(origin, destination, waypoints);
      if (!legs || legs.length === 0) return null;
    }

    // Optimize edilmiş ziyaret sırası (bacakların origin id'leri; dağıtmada en uzak sona eklenir)
    const orderedIds = [];
    for (const leg of legs) {
      if (leg?.id && leg.id !== "school") orderedIds.push(String(leg.id));
    }
    if (!oppositeIsPickUp) orderedIds.push(farthest.id);
    if (orderedIds.length !== stops.length) return null;

    let sumMeters = 0;
    let sumSeconds = 0;
    for (const leg of legs) {
      sumMeters += leg?.distance || 0;
      sumSeconds += leg?.duration || 0;
    }
    if (!Number.isFinite(sumMeters) || !Number.isFinite(sumSeconds) || sumSeconds <= 0) return null;

    // Saatler: anchor geçerliyse kümülatif bacak süreleriyle (haritadaki calculateTransferTimes mantığı)
    const anchor = loadedItem.routeClientArrivalTime;
    let usersdetails = [];
    if (isValidHHmm(anchor)) {
      const timesById = {};
      if (oppositeIsPickUp) {
        // Toplama: anchor = kuruma varış; geriye doğru kümülatif çıkar
        let cum = 0;
        for (let i = legs.length - 1; i >= 0; i--) {
          cum += legs[i]?.duration || 0;
          const id = legs[i]?.id;
          if (id && id !== "school") timesById[String(id)] = subtractSecondsFromTime(anchor, cum);
        }
      } else {
        // Dağıtma: anchor = kurumdan çıkış; ileri doğru kümülatif ekle (bacağın VARIŞ noktasına)
        let cum = 0;
        for (let i = 0; i < legs.length; i++) {
          cum += legs[i]?.duration || 0;
          const destId = i < legs.length - 1 ? legs[i + 1]?.id : farthest.id;
          if (destId && destId !== "school") timesById[String(destId)] = addSecondsToTime(anchor, cum);
        }
      }
      usersdetails = orderedIds
        .filter((id) => timesById[id])
        .map((id) => ({ user: id, transfertime: timesById[id] }));
    }

    return {
      users: orderedIds,
      usersdetails,
      totalDistance: Math.round((sumMeters / 1000) * 10) / 10,
      totalDuration: Math.max(1, Math.round(sumSeconds / 60)),
    };
  } catch (err) {
    return null;
  }
};

const buildPersonelServicePayload = (loadedItem, oppositeRouteType, config) => {
  const payload = {
    name: loadedItem.name,
    school: loadedItem.school?.id || loadedItem.school,
    drivers: loadedItem.drivers?.map((d) => d.id || d) || [],
    plate: loadedItem.plate || "",
    starttime: safeGetTime(loadedItem.starttime, "00:00"),
    stoptime: safeGetTime(loadedItem.stoptime, "23:59"),
    users: loadedItem.users?.map((u) => u.id || u) || [],
    usersdetails: loadedItem.usersdetails || [],
    useUsersDetails: loadedItem.useUsersDetails || false,
    routeType: oppositeRouteType,
    isActive: loadedItem.isActive !== false,
    routeClientArrivalTime: loadedItem.routeClientArrivalTime || undefined,
    totalDistance: loadedItem.totalDistance || 0,
    totalDuration: loadedItem.totalDuration || 0,
  };

  if (config?.hasShift) {
    payload.isshift = true;
    payload.shift = loadedItem.shift || undefined;
  }

  if (config?.hasDate) {
    payload.date = loadedItem.date || undefined;
  }

  if (config?.serviceType) {
    payload.serviceType = config.serviceType;
  }

  return payload;
};

const buildVehicleRoutePayload = (loadedItem, oppositeRouteType) => {
  return {
    name: loadedItem.name,
    school: loadedItem.school?.id || loadedItem.school,
    driver: loadedItem.driver?.map((d) => d.id || d) || [],
    plate: loadedItem.plate || "",
    starttime: safeGetTime(loadedItem.starttime, "00:00"),
    stoptime: safeGetTime(loadedItem.stoptime, "23:59"),
    student: reversedIds(loadedItem.student),
    // Fresh marker only: passenger times are wrong for the opposite direction, and copying the
    // source marker's "2" (transfer done) would hide the NEW route from mobile. Do not reverse
    // the whole routelocations array — marker must stay at [0].
    routelocations: [{ student: "2", transfertime: "1" }],
    routeClientArrivalTime: loadedItem.routeClientArrivalTime || undefined,
    isActive: loadedItem.isActive !== false,
    useUsersDetails: loadedItem.useUsersDetails || false,
    routeType: oppositeRouteType,
  };
};

// Bundled opposite: reverse the mixed student+school stops chain (no optimization). Times/metrics
// are omitted — they belong to the source direction. starttime/stoptime omitted (morning≠evening).
const buildBundledRoutePayload = (loadedItem, oppositeRouteType) => {
  const reversedStops = [...(loadedItem.stops || [])]
    .reverse()
    .map((s) => ({
      kind: s.kind,
      student: s.kind === "STUDENT" ? toId(s.student) : undefined,
      school: s.kind === "SCHOOL" ? toId(s.school) : undefined,
      time: "",
    }))
    .filter((s) => (s.kind === "STUDENT" && s.student) || (s.kind === "SCHOOL" && s.school));

  const studentFromStops = reversedStops.filter((s) => s.kind === "STUDENT").map((s) => s.student);

  return {
    name: loadedItem.name,
    routeType: oppositeRouteType,
    driver: loadedItem.driver?.map((d) => d.id || d) || [],
    plate: loadedItem.plate || "",
    student: studentFromStops.length ? studentFromStops : reversedIds(loadedItem.student),
    stops: reversedStops,
    serviceDays: Array.isArray(loadedItem.serviceDays) ? loadedItem.serviceDays : [],
    useUsersDetails: loadedItem.useUsersDetails !== false,
    isActive: loadedItem.isActive !== false,
  };
};

const VARIANT_CONFIG = {
  personelservice: {
    endpoint: "personelservices",
    itemType: "personelservice",
    buildPayload: buildPersonelServicePayload,
  },
  vehicleRoute: {
    endpoint: "vehicleroutes",
    itemType: "vehicleRoute",
    buildPayload: buildVehicleRoutePayload,
  },
  bundledroute: {
    endpoint: "bundled-route",
    itemType: "bundledRoute",
    buildPayload: buildBundledRoutePayload,
  },
};

const CreateOppositeServiceButton = ({ loadedItem, isNewItem, InsertItem, gotoItem, serviceVariant, config }) => {
  const { t } = useTranslation();
  const { isLoading, sendRequest } = useHttpClient();
  const { getDirectionSingle, getDirectionsWithWaypoints } = useBackend();
  const [showModal, setShowModal] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  const currentRouteType = loadedItem?.routeType;
  const oppositeRouteType = getOppositeRouteType(currentRouteType);
  const isPickUp = checkIsPickUp(currentRouteType);

  const variantConfig = VARIANT_CONFIG[serviceVariant];

  // Only show button for existing items with a valid route type
  if (isNewItem || !loadedItem || !oppositeRouteType || !variantConfig) {
    return null;
  }

  const buttonLabel = isPickUp ? t("createDropOffVersion") : t("createPickUpVersion");
  const oppositeLabel = isPickUp ? t("dropOff") : t("pickUp");

  const handleConfirm = async () => {
    const { endpoint, itemType, buildPayload } = variantConfig;
    let payload = buildPayload(loadedItem, oppositeRouteType, config);

    // Personel servislerinde karşıt servis, "Optimize Et"e basılmışçasına hazır gelir:
    // optimize sıra + hesaplanmış transfer saatleri + güncel totalDistance/totalDuration.
    // Hesap başarısız olursa servis yine oluşturulur; ancak kaynak servisin saatleri
    // KOPYALANMAZ (karşıt yön için yanlış olurlar — örn. sabah biniş saatlerinin
    // akşam dağıtmasına taşınması).
    if (serviceVariant === "personelservice") {
      setIsOptimizing(true);
      const extras = await computeOptimizedOppositeExtras({
        loadedItem,
        oppositeIsPickUp: !isPickUp,
        getDirectionSingle,
        getDirectionsWithWaypoints,
      });
      setIsOptimizing(false);

      if (extras) {
        payload = { ...payload, ...extras };
      } else {
        payload = { ...payload, usersdetails: [] };
      }
    }

    const url = `${process.env.REACT_APP_BACKEND_URL}/${endpoint}`;

    try {
      const responseData = await sendRequest(url, "POST", JSON.stringify(payload));

      if (responseData?.[itemType]) {
        if (InsertItem) {
          InsertItem(responseData[itemType]);
        }
        window.toast.success(t("oppositeServiceCreated"));
        if (gotoItem) {
          gotoItem(responseData[itemType].id);
        }
      }
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code, { names: err.message }));
      } else if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  return (
    <React.Fragment>
      <AreYouSureModal
        showModal={showModal}
        text={t("createOppositeServiceConfirm", { type: oppositeLabel })}
        onClose={() => setShowModal(false)}
        onConfirmed={handleConfirm}
      />
      <Button type="button" size="small" onClick={() => setShowModal(true)} disabled={isLoading || isOptimizing}>
        {buttonLabel}
      </Button>
    </React.Fragment>
  );
};

export default CreateOppositeServiceButton;
